/**
 * Rax Persona List
 *
 * Displays active personas and the bunk room (resting personas).
 * Manage your team - wake, rest, archive, delete.
 */

import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi, type Persona, type PersonaStatus } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

@customElement("rax-persona-list")
export class RaxPersonaList extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }
      
      .container {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
        overflow: hidden;
      }
      
      .section-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        background: var(--bg-elevated);
      }
      
      .section-title {
        font-size: var(--font-lg);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .section-count {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
        font-weight: normal;
      }
      
      .add-btn {
        background: var(--rax-yellow);
        border: none;
        border-radius: var(--radius-md);
        padding: var(--space-2) var(--space-3);
        font-size: var(--font-sm);
        font-weight: 600;
        color: #000;
        cursor: pointer;
      }
      
      .add-btn:hover {
        background: var(--rax-yellow-hover);
      }
      
      .persona-list {
        display: flex;
        flex-direction: column;
      }
      
      .persona-item {
        display: flex;
        align-items: center;
        gap: var(--space-4);
        padding: var(--space-4);
        border-bottom: 1px solid var(--border-color);
        cursor: pointer;
        transition: background var(--transition-fast);
      }
      
      .persona-item:last-child {
        border-bottom: none;
      }
      
      .persona-item:hover {
        background: var(--bg-hover);
      }
      
      .persona-item.resting {
        opacity: 0.6;
      }
      
      .persona-avatar {
        width: 48px;
        height: 48px;
        border-radius: var(--radius-md);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1.5rem;
        flex-shrink: 0;
      }
      
      .persona-info {
        flex: 1;
        min-width: 0;
      }
      
      .persona-name {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .persona-role {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
      }
      
      .persona-skills {
        display: flex;
        gap: var(--space-1);
        margin-top: var(--space-1);
        flex-wrap: wrap;
      }
      
      .skill-tag {
        font-size: var(--font-xs);
        background: var(--bg-subtle);
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        color: var(--text-tertiary);
      }
      
      .status-badge {
        font-size: var(--font-xs);
        font-weight: 600;
        padding: 2px 6px;
        border-radius: var(--radius-sm);
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      .status-badge.active {
        background: rgba(16, 185, 129, 0.2);
        color: #10b981;
      }
      
      .status-badge.resting {
        background: rgba(107, 114, 128, 0.2);
        color: #6b7280;
      }
      
      .persona-actions {
        display: flex;
        gap: var(--space-2);
        flex-shrink: 0;
      }
      
      .action-btn {
        background: transparent;
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        padding: var(--space-2);
        cursor: pointer;
        color: var(--text-tertiary);
        transition: all var(--transition-fast);
        font-size: var(--font-sm);
      }
      
      .action-btn:hover {
        border-color: var(--text-tertiary);
        color: var(--text-primary);
      }
      
      .action-btn.wake {
        border-color: #10b981;
        color: #10b981;
      }
      
      .action-btn.wake:hover {
        background: rgba(16, 185, 129, 0.1);
      }
      
      /* Bunk room section */
      .bunk-room {
        margin-top: var(--space-4);
        border-top: 1px solid var(--border-color);
        background: var(--bg-subtle);
      }
      
      .bunk-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: var(--space-3) var(--space-4);
        cursor: pointer;
      }
      
      .bunk-title {
        font-size: var(--font-md);
        font-weight: 500;
        color: var(--text-secondary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }
      
      .bunk-count {
        font-size: var(--font-sm);
        color: var(--text-tertiary);
      }
      
      .bunk-expand {
        color: var(--text-tertiary);
        transition: transform var(--transition-fast);
      }
      
      .bunk-expand.expanded {
        transform: rotate(180deg);
      }
      
      .bunk-list {
        padding: 0 var(--space-4) var(--space-4);
      }
      
      .bunk-item {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-3);
        background: var(--bg-secondary);
        border-radius: var(--radius-md);
        margin-bottom: var(--space-2);
      }
      
      .bunk-item:last-child {
        margin-bottom: 0;
      }
      
      .bunk-avatar {
        width: 36px;
        height: 36px;
        border-radius: var(--radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        opacity: 0.6;
      }
      
      .bunk-info {
        flex: 1;
      }
      
      .bunk-name {
        font-size: var(--font-sm);
        font-weight: 500;
        color: var(--text-secondary);
      }
      
      .bunk-role {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }
      
      .empty-state {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }
      
      .empty-icon {
        font-size: 3rem;
        margin-bottom: var(--space-4);
        opacity: 0.5;
      }
      
      .empty-title {
        font-size: var(--font-lg);
        color: var(--text-secondary);
        margin-bottom: var(--space-2);
      }
      
      .empty-desc {
        font-size: var(--font-sm);
      }
      
      .loading {
        padding: var(--space-8);
        text-align: center;
        color: var(--text-tertiary);
      }
    `,
  ];

  @state() private personas: Persona[] = [];
  @state() private loading = true;
  @state() private error: string | null = null;
  @state() private bunkExpanded = false;
  @state() private actionInProgress: Record<string, boolean> = {};

  override connectedCallback() {
    super.connectedCallback();
    this.loadPersonas();
  }

  private async loadPersonas() {
    this.loading = true;
    this.error = null;

    try {
      const response = await raxApi.listPersonas();
      if (response.success && response.data) {
        this.personas = response.data;
      } else {
        this.error = response.error ?? "Failed to load personas";
      }
    } catch (err) {
      console.error("[PersonaList] Error loading:", err);
      this.error = err instanceof Error ? err.message : "Failed to load";
    } finally {
      this.loading = false;
    }
  }

  private get activePersonas(): Persona[] {
    return this.personas.filter((p) => p.status === "active");
  }

  private get restingPersonas(): Persona[] {
    return this.personas.filter((p) => p.status === "resting");
  }

  private getRoleIcon(role: string): string {
    const icons: Record<string, string> = {
      engineer: "👩‍💻",
      pm: "📋",
      devops: "🔧",
      analyst: "📊",
      writer: "✍️",
      support: "🎧",
      custom: "⚡",
      coordinator: "👔",
    };
    return icons[role] ?? "⚡";
  }

  private async setStatus(id: string, status: PersonaStatus) {
    this.actionInProgress = { ...this.actionInProgress, [id]: true };

    try {
      const response = await raxApi.setPersonaStatus(id, status);
      if (response.success && response.data) {
        this.personas = this.personas.map((p) => (p.id === id ? response.data! : p));
      }
    } catch (err) {
      console.error("[PersonaList] Error setting status:", err);
    } finally {
      this.actionInProgress = { ...this.actionInProgress, [id]: false };
    }
  }

  private handleAddClick() {
    this.dispatchEvent(new CustomEvent("add-persona", { bubbles: true, composed: true }));
  }

  private handlePersonaClick(persona: Persona) {
    this.dispatchEvent(
      new CustomEvent("edit-persona", {
        detail: { persona },
        bubbles: true,
        composed: true,
      }),
    );
  }

  public async refresh() {
    await this.loadPersonas();
  }

  override render() {
    return html`
      <div class="container">
        <div class="section-header">
          <div class="section-title">
            Your Team
            ${this.activePersonas.length > 0 ? html`<span class="section-count">(${this.activePersonas.length})</span>` : nothing}
          </div>
          <button class="add-btn" @click=${this.handleAddClick}>+ Add Persona</button>
        </div>

        ${
          this.loading
            ? html`
                <div class="loading">Loading team...</div>
              `
            : this.error
              ? html`<div class="empty-state"><div class="empty-desc">${this.error}</div></div>`
              : this.activePersonas.length === 0
                ? html`
                    <div class="empty-state">
                      <div class="empty-icon">👥</div>
                      <div class="empty-title">No team members yet</div>
                      <div class="empty-desc">Add personas to build your AI team</div>
                    </div>
                  `
                : html`
                  <div class="persona-list">
                    ${this.activePersonas.map((p) => this.renderPersonaItem(p))}
                  </div>
                `
        }

        ${this.restingPersonas.length > 0 ? this.renderBunkRoom() : nothing}
      </div>
    `;
  }

  private renderPersonaItem(persona: Persona) {
    const inProgress = this.actionInProgress[persona.id] ?? false;

    return html`
      <div class="persona-item" @click=${() => this.handlePersonaClick(persona)}>
        <div class="persona-avatar" style="background: ${persona.appearance.hairColor}22">
          ${this.getRoleIcon(persona.role)}
        </div>
        <div class="persona-info">
          <div class="persona-name">
            ${persona.name}
            <span class="status-badge active">Active</span>
          </div>
          <div class="persona-role">${persona.role}</div>
          <div class="persona-skills">
            ${persona.skills.enabled.slice(0, 4).map((s) => html`<span class="skill-tag">${s}</span>`)}
            ${persona.skills.enabled.length > 4 ? html`<span class="skill-tag">+${persona.skills.enabled.length - 4}</span>` : nothing}
          </div>
        </div>
        <div class="persona-actions" @click=${(e: Event) => e.stopPropagation()}>
          <button
            class="action-btn"
            title="Send to bunk room"
            ?disabled=${inProgress}
            @click=${() => this.setStatus(persona.id, "resting")}
          >
            💤
          </button>
        </div>
      </div>
    `;
  }

  private renderBunkRoom() {
    return html`
      <div class="bunk-room">
        <div class="bunk-header" @click=${() => (this.bunkExpanded = !this.bunkExpanded)}>
          <div class="bunk-title">
            💤 Bunk Room
            <span class="bunk-count">(${this.restingPersonas.length} resting)</span>
          </div>
          <span class="bunk-expand ${this.bunkExpanded ? "expanded" : ""}">▼</span>
        </div>

        ${
          this.bunkExpanded
            ? html`
              <div class="bunk-list">
                ${this.restingPersonas.map((p) => this.renderBunkItem(p))}
              </div>
            `
            : nothing
        }
      </div>
    `;
  }

  private renderBunkItem(persona: Persona) {
    const inProgress = this.actionInProgress[persona.id] ?? false;

    return html`
      <div class="bunk-item">
        <div class="bunk-avatar" style="background: ${persona.appearance.hairColor}22">
          ${this.getRoleIcon(persona.role)}
        </div>
        <div class="bunk-info">
          <div class="bunk-name">${persona.name}</div>
          <div class="bunk-role">${persona.role}</div>
        </div>
        <button class="action-btn wake" ?disabled=${inProgress} @click=${() => this.setStatus(persona.id, "active")}>
          Wake Up
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-persona-list": RaxPersonaList;
  }
}
